/**
 * Sonic Prospect pricing for the console: one plan, every feature for every
 * account; only Qualified Prospects are paid. Enterprise is a sales contract.
 */

/**
 * One commercial line for the console, onboarding, Faturalama, and plans.
 * First 5 free, leads 6 through 1,000 at $5.99, then $9.99.
 * defaultCap is the Shopify usage cap (DEFAULT_CAPPED_AMOUNT_USD).
 * A standard conversation (fairUseAnswers per visitor in 24 hours) is included;
 * extended conversations answer up to extendedMaxAnswers at extendedAnswerPrice
 * per answer past fairUseAnswers, within a monthly budget (USD).
 */
export const SONIC_PROSPECT_PRICING = {
  freeLifetimeQp: 5,
  tier1Limit: 1000,
  tier1Price: 5.99,
  tier2Price: 9.99,
  defaultCap: 1000,
  fairUseAnswers: 20,
  extendedMaxAnswers: 60,
  extendedAnswerPrice: 0.01,
  extendedDefaultBudget: 20,
  shopifyAppHandle: "sonic-prospect-1",
  shopifyAppStoreUrl: "https://shopify.com/apps",
};

export const PROSPECT_OUTCOME_PRICING = {
  launchRate: SONIC_PROSPECT_PRICING.tier1Price,
  standardRate: SONIC_PROSPECT_PRICING.tier2Price,
  launchThreshold: SONIC_PROSPECT_PRICING.tier1Limit,
  freeLifetimeQp: SONIC_PROSPECT_PRICING.freeLifetimeQp,
};

/** @deprecated Use SONIC_PROSPECT_PRICING. Kept so older imports keep working. */
export const SHOPIFY_PROSPECT_PRICING = {
  freeLifetime: SONIC_PROSPECT_PRICING.freeLifetimeQp,
  introRate: SONIC_PROSPECT_PRICING.tier1Price,
  standardRate: SONIC_PROSPECT_PRICING.tier2Price,
};

/** @deprecated Monthly/annual subscription billing removed from Prospect UI. */
export const PROSPECT_BILLING_MONTHLY = "monthly";
/** @deprecated */
export const PROSPECT_BILLING_ANNUAL = "annual";

export function normalizeProspectBillingCycle(cycle) {
  return cycle === PROSPECT_BILLING_ANNUAL
    ? PROSPECT_BILLING_ANNUAL
    : PROSPECT_BILLING_MONTHLY;
}

export function formatProspectUsd(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return "";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: n % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(n);
}

/**
 * Estimate spend for N qualified prospects (cumulative account economics).
 */
export function formatProspectUsdTr(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return "";
  return new Intl.NumberFormat("tr-TR", {
    style: "currency",
    currency: "USD",
  }).format(n);
}

export function estimateProspectOutcomeSpend(qualifiedProspects) {
  const qty = Math.max(0, Math.floor(Number(qualifiedProspects) || 0));
  const { freeLifetimeQp, tier1Limit, tier1Price, tier2Price } = SONIC_PROSPECT_PRICING;
  const freeQty = Math.min(qty, freeLifetimeQp);
  const tier1Qty = Math.min(
    Math.max(0, qty - freeLifetimeQp),
    Math.max(0, tier1Limit - freeLifetimeQp),
  );
  const tier2Qty = Math.max(0, qty - tier1Limit);
  const launchSpend = tier1Qty * tier1Price;
  const standardSpend = tier2Qty * tier2Price;
  return {
    quantity: qty,
    freeQty,
    freeAllowance: freeLifetimeQp,
    launchQty: tier1Qty,
    standardQty: tier2Qty,
    launchRate: tier1Price,
    standardRate: tier2Price,
    launchThreshold: tier1Limit,
    launchSpend,
    standardSpend,
    total: launchSpend + standardSpend,
  };
}

export function prospectOutcomeLaunchLabel() {
  return `${formatProspectUsd(PROSPECT_OUTCOME_PRICING.launchRate)} / Qualified Prospect`;
}

export function prospectOutcomeStandardLabel() {
  return `${formatProspectUsd(PROSPECT_OUTCOME_PRICING.standardRate)} / Qualified Prospect`;
}
